import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {TeammateAvatar} from '../../components/common/avatars/teammateAvatar';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {SettingsProfileUrl} from '../../models/url/settingsUrlModel';
import {deleteUserAvatar, saveUserAction} from '../../store/data/teammates/teammatesActions';
import {getOtherTeammatesAliases, getUser} from '../../store/data/teammates/teammatesSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {
  SettingsFooter,
  StyledSettingsContent,
  StyledSettingsMain as SettingsMain,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {parseApiError} from '../rules/ruleHelpers';
import {settingsHorizontalPaddingStyles} from '../settingsCommonConstants';
import {SettingsHeader} from '../settingsHeader';
import {SettingsProfile2FA} from './settingsProfile2FA';
import {SettingsProfileEmail} from './settingsProfileEmail';
import {SettingsProfilePassword} from './settingsProfilePassword';
import {SettingsUserAvatarDialog} from './settingsUserAvatarDialog';

/*
 * Props.
 */

interface SettingsProfileProps {
  url: SettingsProfileUrl;
  panelId: SettingsPanelId;
}

/*
 * Style.
 */

const StyledProfileAvatarSectionDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 20px;
  align-items: center;
`;

const StyledAvatarEditButton = styled(ChromelessButton)`
  position: relative;
  width: 100px;
  height: 100px;
  overflow: hidden;
  border-radius: 50%;
`;

const StyledProfileOverlayAvatarDiv = styled.div<{$hasHover: boolean}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(p) => p.theme.alphas.black50};
  color: ${(p) => p.theme.greys.white};
  text-align: center;
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.medium};
  z-index: 1;
  opacity: 0;
  line-height: 100px;

  ${StyledAvatarEditButton}:hover & {
    opacity: ${(p) => (p.$hasHover ? 1 : 0)};
  }
`;

const StyledAvatarTextDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledAvatarTextCopyP = styled.p`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  line-height: 1.5514285714;
  margin: 0;
`;

const StyledButtonsDiv = styled.div`
  display: flex;
  gap: 6px;
`;

const StyledFieldsDiv = styled.div`
  margin-top: 36px;
  margin-bottom: 12px;
  max-width: 480px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: 16px;
  grid-row-gap: 7px;
`;

const StyledSettingsMain = styled(SettingsMain)`
  ${settingsHorizontalPaddingStyles};
  max-width: 710px;
`;

/*
 * Component.
 */

export const SettingsProfile: FC<SettingsProfileProps> = (props) => {
  const user = useAppSelector(getUser);
  const otherAliases = useAppSelector(getOtherTeammatesAliases);
  const dispatch = useAppDispatch();
  const [givenName, setGivenName] = useState(user.givenName);
  const [familyName, setFamilyName] = useState(user.familyName);
  const [alias, setAlias] = useState(user.alias);
  const [isAvatarDialogOpen, setIsAvatarDialogOpen] = useState(false);

  const showFooter = useMemo(
    () => !(givenName === user.givenName && familyName === user.familyName && alias === user.alias),
    [alias, familyName, givenName, user.alias, user.familyName, user.givenName],
  );

  const isFamilyNameValid = useMemo(() => familyName.trim().length > 0, [familyName]);
  const isGivenNameValid = useMemo(() => givenName.trim().length > 0, [givenName]);
  const isAliasValid = useMemo(() => alias.match(/^[a-z0-9][a-z0-9_.]{0,62}[a-z0-9]$/i), [alias]);
  const isAliasAvailable = useMemo(() => !otherAliases.has(alias), [alias, otherAliases]);
  const hasAliasError = useMemo(() => !(isAliasValid && isAliasAvailable), [isAliasAvailable, isAliasValid]);

  const canSave = useMemo(
    () => showFooter && isGivenNameValid && isFamilyNameValid && !hasAliasError,
    [hasAliasError, isFamilyNameValid, isGivenNameValid, showFooter],
  );
  const resetState = useCallback(() => {
    setAlias(user.alias);
    setGivenName(user.givenName);
    setFamilyName(user.familyName);
  }, [user.alias, user.familyName, user.givenName]);

  const {showBanner} = useSettingsBanners();

  const saveUser = useCallback(async () => {
    const hasAliasChanged = alias !== user.alias;
    const hasFamilyNameChanged = familyName !== user.familyName;
    const hasGivenNameChanged = givenName !== user.givenName;
    const hasNameChanged = hasFamilyNameChanged || hasGivenNameChanged;

    const makeUpdateMessage = () => {
      if (hasNameChanged && !hasAliasChanged) {
        return <Trans id="settingsprofile-nameupdate">Name has been updated</Trans>;
      }

      if (hasNameChanged && hasAliasChanged) {
        return <Trans id="settingsprofile-nameusernameupdate">Name and username have been updated</Trans>;
      }

      if (hasAliasChanged) {
        return <Trans id="settingsprofile-usernameupdate">Username has been updated</Trans>;
      }

      // Shouldn't happen
      return <Trans id="settingsprofile-informationsupdate">Informations have been updated</Trans>;
    };

    try {
      await dispatch(
        saveUserAction({
          givenName,
          familyName,
          alias,
        }),
      );

      showBanner({
        message: makeUpdateMessage(),
        type: SettingsBannersEnum.SUCCESS,
      });
    } catch (e) {
      showBanner({message: parseApiError(e), type: SettingsBannersEnum.ERROR});
    }
  }, [alias, dispatch, familyName, givenName, showBanner, user.alias, user.familyName, user.givenName]);

  const renderAliasErrorMessage = () => {
    if (!isAliasValid) {
      return (
        <Trans id="settingsprofile-invalidusername">Must contain only letters, digits, and underscores</Trans>
      );
    }

    if (!isAliasAvailable) {
      return (
        <Trans id="settingsprofile-usernametaken">This username is already taken by another teammate</Trans>
      );
    }

    return null;
  };

  const maybeRenderDialogs = () => {
    if (isAvatarDialogOpen) {
      return (
        <SettingsUserAvatarDialog
          onRequestClose={() => {
            setIsAvatarDialogOpen(false);
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      <StyledSettingsWrapper>
        <SettingsHeader>
          <Trans id="settingsprofile-profile">Profile</Trans>
        </SettingsHeader>
        <StyledSettingsMain>
          <StyledSettingsContent>
            <StyledProfileAvatarSectionDiv>
              <StyledAvatarEditButton
                onClick={() => {
                  setIsAvatarDialogOpen(true);
                }}
              >
                <StyledProfileOverlayAvatarDiv $hasHover={!isAvatarDialogOpen}>
                  <Trans id="settingshomeprofile-edit">Edit</Trans>
                </StyledProfileOverlayAvatarDiv>
                <TeammateAvatar size={100} teammate={user} />
              </StyledAvatarEditButton>
              <StyledAvatarTextDiv>
                <StyledAvatarTextCopyP>
                  <Trans id="settingsprofile-avatar">
                    This picture will appear on Front collaboration, not external communication. (file must be
                    less than 1MB).
                  </Trans>
                </StyledAvatarTextCopyP>
                <StyledButtonsDiv>
                  <Button
                    variant={ButtonVariantsEnum.PRIMARY}
                    onClick={() => {
                      setIsAvatarDialogOpen(true);
                    }}
                  >
                    <Trans id="settingsprofile-edit">Edit</Trans>
                  </Button>
                  <Button
                    variant={ButtonVariantsEnum.SECONDARY}
                    onClick={() => {
                      dispatch(deleteUserAvatar());
                    }}
                  >
                    <Trans id="settingsprofile-delete">Delete</Trans>
                  </Button>
                </StyledButtonsDiv>
              </StyledAvatarTextDiv>
            </StyledProfileAvatarSectionDiv>
            <StyledFieldsDiv>
              <TextBox
                defaultValue={givenName}
                onChange={setGivenName}
                isRequired
                hasError={!isGivenNameValid}
                errorMessage={<Trans id="settingsprofile-firstnamerequired">First name is required</Trans>}
                label={<Trans id="settingsprofile-firstname">First name</Trans>}
              />
              <TextBox
                defaultValue={familyName}
                onChange={setFamilyName}
                isRequired
                hasError={!isFamilyNameValid}
                errorMessage={<Trans id="settingsprofile-lastnamerequired">Last name is required</Trans>}
                label={<Trans id="settingsprofile-lastname">Last name</Trans>}
              />
              <TextBox
                defaultValue={alias}
                hasError={hasAliasError}
                errorMessage={renderAliasErrorMessage()}
                onChange={setAlias}
                isRequired
                label={<Trans id="settingsprofile-username">Username</Trans>}
              />
              <TextBox
                defaultValue={user.email}
                isDisabled
                label={<Trans id="settingsprofile-emailaddress">Email address</Trans>}
              />
            </StyledFieldsDiv>
            <SettingsProfileEmail user={user} />
            <SettingsProfilePassword user={user} />
            <SettingsProfile2FA user={user} />
          </StyledSettingsContent>
        </StyledSettingsMain>
        {showFooter && (
          <SettingsFooter>
            <Button isRounded variant={ButtonVariantsEnum.SECONDARY} onClick={resetState}>
              <Trans id="settings-form-cancel">Cancel</Trans>
            </Button>
            <Button isRounded variant={ButtonVariantsEnum.PRIMARY} onClick={saveUser} isDisabled={!canSave}>
              <Trans id="settings-form-save">Save</Trans>
            </Button>
          </SettingsFooter>
        )}
      </StyledSettingsWrapper>
      {maybeRenderDialogs()}
    </>
  );
};
